import { createAsyncThunk } from "@reduxjs/toolkit";
import authServiceInstance from "./auth.service";
import { IAuthRequest, IRegisterUserRequest } from "./AuthRequest";
import IUser from "./IUser";
import HTTPStatus from "../../utils/HTTPStatus";
import { IAuthResponse } from "./IAuthResponse";
import { displayError } from "../../utils/showToastNotification";
import { parseApiError } from "../../utils/parseApiError";
import IUserProfile from "./IUserProfile";
import IEditUserModel from "./IEditUserModel";
import IResetPassword from "./IResetPassword";
import { IAuthPhotoResponse } from "./IAuthPhotoResponse";
import { registerForPushNotifications, unsubscribeFromPush } from "../../utils/pushNotifications";
import { RootState } from "../../store";
import { queryClient } from "../../api/queryClient";
import { localStore } from "../../utils/storage";

/**
 * Session-presence hint for the boot-time refresh gate (see
 * `shouldAttemptRefresh` in index.tsx and the key doc in storage.ts).
 * `true` after any authenticated exchange, `false` only when the session
 * is KNOWN dead (logout, or a refresh the server rejected as unauthorized)
 * — never on transient/network failures, which must stay fail-open.
 */
function recordSessionPresence(hasSession: boolean): void {
  localStore.set("br-had-session", hasSession);
}

// On rejection we always `rejectWithValue(undefined)`. RTK's
// serializability check rejects AxiosError payloads, and no reducer or
// caller in this codebase reads the rejection payload — the user-facing
// error is already surfaced via `displayError(parseApiError(e).message)`.

export const loginUserWithPassword = createAsyncThunk<{ data: IAuthResponse }, IAuthRequest>(
  "auth/login",
  async (authRequest: IAuthRequest, thunkApi) => {
    try {
      const response = await authServiceInstance.login(authRequest);
      // Kick off the push-notification probe out-of-band on success.
      // Lives here (rather than the reducer) because reducers must
      // be pure — calling an async side effect inside an Immer-driven
      // case can fire multiple times under StrictMode and breaks the
      // time-travel devtools.
      //
      // Fire-and-forget by design (the login flow shouldn't wait on a
      // push permission prompt). Errors are swallowed: push is a
      // best-effort opt-in, not load-bearing for login.
      void registerForPushNotifications().catch(() => {
        // intentionally silent — push failures must not surface
        // during a successful login.
      });
      recordSessionPresence(true);
      return response;
    } catch (e) {
      displayError(parseApiError(e).message);
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

export const registerUser = createAsyncThunk<HTTPStatus, IRegisterUserRequest>(
  "auth/register-user.noLoading",
  async (authRequest: IRegisterUserRequest, thunkApi) => {
    try {
      // Return the HTTP status so the fulfilled payload type matches
      // the thunk's declared HTTPStatus return; nothing actually
      // consumes the value today (the only caller checks the request
      // status, not the payload), but the type contract needs to hold.
      return await authServiceInstance.registerUser(authRequest);
    } catch (e) {
      displayError(parseApiError(e).message);
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

export const uploadUserProfilePhoto = createAsyncThunk<
  { data: IAuthPhotoResponse },
  { file: File }
>("auth/upload-profile-photo", async (data: { file: File }, thunkApi) => {
  try {
    const formData = new FormData();
    formData.append("file", data.file);
    return await authServiceInstance.uploadProfilePhoto(formData);
  } catch (e) {
    displayError(parseApiError(e).message);
    return thunkApi.rejectWithValue(undefined);
  }
});

export const updateUserData = createAsyncThunk<{ data: IAuthResponse }, IEditUserModel>(
  "auth/update-user-data",
  async (userData: IEditUserModel, thunkApi) => {
    try {
      return await authServiceInstance.updateProfileData(userData);
    } catch (e) {
      displayError(parseApiError(e).message);
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

export const changePassword = createAsyncThunk<void, IResetPassword>(
  "auth/change-password",
  async (passwordData: IResetPassword, thunkApi) => {
    try {
      return await authServiceInstance.resetPassword(passwordData);
    } catch (e) {
      displayError(parseApiError(e).message);
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

// The cold-boot /me deliberately keeps the refresh-on-401 flow ENABLED.
// Session restore across a full browser restart depends on it: mobile
// browsers cold-boot with the short-lived access cookie expired but a
// perfectly valid refresh cookie, so the boot /me must be allowed to
// 401 → /auth/refresh → retry, or every reopen forces a re-login (the
// old `skipAuthRefresh: isInitialBootstrap` did exactly that). A fresh
// visitor with no cookies pays one doomed /auth/refresh round-trip and
// lands on the login page silently — the toast guard below and the
// boot-aware `onUnauthorized` in index.tsx keep that path quiet.
export const fetchUserInformation = createAsyncThunk<{ data: IUser }, void, { state: RootState }>(
  "auth/me.noLoading",
  async (_: void, thunkApi) => {
    const state = thunkApi.getState();
    // `pending` has already moved bootStatus "idle" → "bootstrapping",
    // so "not ready" identifies the cold boot.
    const isInitialBootstrap = state.auth.bootStatus !== "ready";
    try {
      const response = await authServiceInstance.me();
      recordSessionPresence(true);
      return response;
    } catch (e) {
      const err = parseApiError(e);
      // A 401 here means "no restorable session" (the interceptor has
      // already tried the refresh flow) — expected, never toasted.
      // Compare on `code`, not on the message string, so any future
      // copy change to the friendly-message map doesn't silently
      // re-enable the boot-time toast.
      if (err.code !== "UNAUTHORIZED" && !isInitialBootstrap) {
        displayError(err.message);
      }
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

export const fetchUserProfile = createAsyncThunk<{ data: IUserProfile }>(
  // The `:load` suffix used to plug into globalSlice's action-type
  // matcher; that slice was deleted in Phase 5 along with its last
  // consumer (AdminDashboard's pre-rewrite global loader).
  "auth/profile",
  async (_: void, thunkApi) => {
    try {
      return await authServiceInstance.profile();
    } catch (e) {
      const err = parseApiError(e);
      if (err.code !== "UNAUTHORIZED") displayError(err.message);
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

export const refreshUserInformation = createAsyncThunk<HTTPStatus, void>(
  "auth/refresh",
  async (_, thunkApi) => {
    try {
      const status = await authServiceInstance.refresh();
      recordSessionPresence(true);
      return status;
    } catch (e) {
      // The server explicitly rejecting the refresh token means the
      // session is dead — record that so the next cold boot skips the
      // doomed refresh attempt. Transient failures (network, 5xx) keep
      // the hint untouched: fail-open, the session may still be valid.
      if (parseApiError(e).code === "UNAUTHORIZED") {
        recordSessionPresence(false);
      }
      // Don't toast — the interceptor will route this to logout if
      // refresh genuinely fails. Toasting here causes "session expired"
      // spam during normal token rotation.
      return thunkApi.rejectWithValue(undefined);
    }
  },
);

/**
 * Wipe every React Query cache. Called on every logout path
 * (user-initiated, logout-all, and the axios interceptor's silent
 * 401 fall-through) so the next sign-in on the same browser never
 * sees the previous user's data.
 *
 * `clear()` (not `removeQueries`) so even queries that are still
 * mounted at logout time (the protected route is about to unmount)
 * lose their cached payloads — the next mount will fetch fresh.
 */
function clearAllCaches(): void {
  queryClient.clear();
}

export const logoutUser = createAsyncThunk<void>("auth/logout", async (_: void, thunkApi) => {
  recordSessionPresence(false);
  try {
    unsubscribeFromPush();
    await authServiceInstance.logout();
  } catch {
    // Logout is best-effort: even if the network call fails, we still
    // want the slice to flip to unauthenticated. Swallow without
    // toasting — the interceptor's NO_REFRESH_PATHS guard prevents
    // the loop that used to spam errors here.
    clearAllCaches();
    return thunkApi.rejectWithValue(undefined);
  }
  clearAllCaches();
});

export const logoutAllSessions = createAsyncThunk<void>(
  "auth/logout-all",
  async (_: void, thunkApi) => {
    recordSessionPresence(false);
    try {
      unsubscribeFromPush();
      await authServiceInstance.logoutAll();
    } catch {
      clearAllCaches();
      return thunkApi.rejectWithValue(undefined);
    }
    clearAllCaches();
  },
);
